import React, { useState } from "react";
import { Chip, Stack } from "@mui/material";
import { useFormContext } from "react-hook-form";

import {
  AttachmentSection,
  FinancialPeriodSelect,
  HubHookFormSelect,
  HubMaterialHookSelect,
} from "../../../components";
import { usePutQuoteStage } from "../../../hooks/react-query/useSalesData";
import { QUOTE_STAGE_LABELS } from "../../../constants/salesStages";
import QuoteStageActionsDialog from "../../../containers/SalesKanban/QuoteStageActionsDialog";
import {
  FILE_CATEGORIES,
  RESOURCE_TYPES,
} from "../../../constants/attachments";

const StatusSelectRender = ({ label, flow = "grey" }) => (
  <Chip
    variant="colorTag"
    label={label}
    flow={flow}
    sx={{ fontSize: 10, fontWeight: 500 }}
  />
);

const statusRenderValue = (value, options) => {
  const op = options?.find((o) => o.id === value);
  return <StatusSelectRender {...op} />;
};

const SettingsTab = ({ quoteId }) => {
  const { watch, getValues } = useFormContext();
  const { mutate: updateStage } = usePutQuoteStage();
  const [prompt, setPrompt] = useState(null);

  const locationId = watch("locationId");
  const customerId = watch("customerId");
  const partId = watch("partId");

  const handleStageChange = (stage) => {
    if (!quoteId) return;
    const stageInfo = QUOTE_STAGE_LABELS.find((s) => s.id === stage);
    updateStage(
      { id: quoteId, stage },
      {
        onSuccess: () => {
          if (!stageInfo?.promptUpload && !stageInfo?.promptCreate) return;
          const v = getValues();
          setPrompt({
            stage,
            quote: {
              id: quoteId,
              quoteNumber: v.quoteNumber,
              customerId: v.customerId,
              partId: v.partId,
              partName: v.partName,
              partNumber: v.partNumber,
              weight: v.weight,
              partsPerTree: v.partsPerTree,
              price: v.total ?? v.price,
            },
          });
        },
      },
    );
  };

  const itemSx = { flexShrink: 0 };

  return (
    <Stack
      gap="15px"
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        "::-webkit-scrollbar": {
          display: "none !important",
        },
      }}
    >
      <Stack sx={itemSx}>
        <FinancialPeriodSelect locationId={locationId} />
      </Stack>
      <Stack direction="row" gap="10px" sx={itemSx}>
        <HubMaterialHookSelect
          name="partWaxId"
          label="Part Wax"
          showOnlyGroup="Wax"
          enableSearch
        />
        <HubMaterialHookSelect
          name="treeWaxId"
          label="Tree Wax"
          showOnlyGroup="Wax"
          enableSearch
        />
      </Stack>
      <Stack sx={itemSx}>
        <HubHookFormSelect
          name="stage"
          label="Status"
          options={QUOTE_STAGE_LABELS}
          RenderComponent={StatusSelectRender}
          renderValue={(value) => statusRenderValue(value, QUOTE_STAGE_LABELS)}
          onChange={handleStageChange}
          required
        />
      </Stack>
      <Stack sx={itemSx}>
        <AttachmentSection
          resourceType={RESOURCE_TYPES.QUOTE}
          resourceId={quoteId}
          categories={[
            FILE_CATEGORIES.MODEL,
            FILE_CATEGORIES.SETUP,
            FILE_CATEGORIES.VENDOR_QUOTE,
            FILE_CATEGORIES.CUSTOMER_DRAWING,
          ]}
          metadataDefaults={{
            customerId: customerId || null,
            partId: partId || null,
          }}
          compact
        />
      </Stack>
      {prompt && (
        <QuoteStageActionsDialog
          quote={prompt.quote}
          stage={prompt.stage}
          onClose={() => setPrompt(null)}
        />
      )}
    </Stack>
  );
};

export default SettingsTab;
